import React, { useEffect, useRef } from 'react';
import {
  Grid, Card, Switch, SpeedDialAction, SpeedDial, Button, Dialog, DialogContent, DialogTitle, IconButton, Box,
  CircularProgress, TextField, DialogActions, DialogContentText, FormControl, InputLabel, Select, MenuItem, Rating
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { getErrorMessage, getDownloadURL, getMetadata } from '../../config/firebase';
import {
  cpfOrCnpj,
  phoneNumber,
  toBrazilDate,
  toBrazilDateTime
} from '../../components/mask';
import driverService from '../../services/driverService';
import subsidiaryService from '../../services/subsidiaryService';
import Driver from '../../models/interfaces/driver';
import { StatusDriverText } from '../../models/enums/statusDriver';
import { CategoryDriverText } from '../../models/enums/categoryDriver';
import NoData from '../../components/noData';
import avatarImg from '../../assets/images/avatar.png';
import { useAuth } from '../../contexts/auth';
import ConfirmDialog from '../../components/confirmDialog';
import TermModalComponent from './components/termModalComponent';
import Subsidiary from '../../models/interfaces/subsidiary';
import UseStyles from '../../components/styles/loadingButton';

interface Props {
  open: boolean;
  driverId: string;
  onClose: () => void;
  onEdit: () => void;
}

interface File {
  id: string;
  url: string;
  name: string;
  size: number;
  icon: IconProp;
  updated: Date;
}

const DRIVER_DOCUMENTS = [
  { fileName: 'chn.jpg', label: 'CNH' },
  { fileName: 'rgfront.jpg', label: 'RG (frente)' },
  { fileName: 'rgback.jpg', label: 'RG (verso)' },
  { fileName: 'veiculodoc.jpg', label: 'Documento do veículo' },
  { fileName: 'addressdoc.jpg', label: 'Comprovante de endereço' }
];

const documentLabel = (fileName: string) => (
  DRIVER_DOCUMENTS.find((item) => item.fileName === fileName)?.label ?? fileName
);

interface DriverRatingAverage {
  driverId: string;
  driverName: string | null;
  averageRating: number;
  totalRatings: number;
}

const formatDriverRatingStatus = (
  rating: DriverRatingAverage | null,
  unavailable: boolean
): string | null => {
  if (unavailable) return 'Avaliação indisponível';
  if (!rating) return null;
  if (rating.totalRatings === 0) return 'Sem avaliações';
  return null;
};

const formatDriverRatingLabel = (rating: DriverRatingAverage): string => {
  const countLabel = rating.totalRatings === 1 ? 'avaliação' : 'avaliações';
  const average = Number(rating.averageRating).toFixed(1);
  return `${average} de 5, ${rating.totalRatings} ${countLabel}`;
};

const DriverDetailsModal = ({
  open,
  driverId,
  onClose,
  onEdit
}: Props) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [model, setModel] = React.useState<Driver>();
  const [docs, setDocs] = React.useState<File[]>([]);
  const [terms, setTerms] = React.useState<any[]>([]);
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [countClicks, setCountClicks] = React.useState<number>(3);
  const [modalData, setModalData] = React.useState<any>();
  const [openModal, setOpenModal] = React.useState(false);
  const [subsidiary, setSubsidiary] = React.useState<Subsidiary>();

  // Estados para análise de documentos (mantidos do código antigo)
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [openModalJustification, setOpenModalJustification] = React.useState(false);
  const [justification, setJustification] = React.useState<string>('');
  const [isUploadingPhoto, setIsUploadingPhoto] = React.useState(false);
  const [isUploadingDocs, setIsUploadingDocs] = React.useState(false);
  const [ratingAverage, setRatingAverage] = React.useState<DriverRatingAverage | null>(null);
  const [ratingUnavailable, setRatingUnavailable] = React.useState(false);
  const [docType, setDocType] = React.useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docsInputRef = useRef<HTMLInputElement>(null);
  const classes = UseStyles();

  const loadDocs = async (id: string) => {
    const res = await driverService.getDocs(id);
    const resultDocs = await Promise.all(res.map(async (x: any) => {
      const url = await getDownloadURL(x);
      const metaData = await getMetadata(x);
      let icon: IconProp;
      switch (metaData.contentType) {
        case 'application/pdf':
          icon = ['far', 'file-pdf'] as IconProp;
          break;
        case 'image/jpeg':
        case 'image/png':
        case 'image/webp':
          icon = ['far', 'file-image'] as IconProp;
          break;
        default:
          icon = ['far', 'file'] as IconProp;
          break;
      }
      return {
        id: metaData.generation,
        url,
        name: x.name,
        size: metaData.size,
        icon,
        updated: new Date(metaData.updated)
      };
    }));
    setDocs(resultDocs);
  };

  useEffect(() => {
    let active = true;
    setRatingAverage(null);
    setRatingUnavailable(false);

    if (driverId && open) {
      driverService.getRatingAverage(driverId).then((data) => {
        if (active) setRatingAverage(data);
      }).catch(() => {
        if (active) setRatingUnavailable(true);
      });

      driverService.getById(driverId).then(async (item: Driver) => {
        setModel(item);
        setCountClicks(3);
        subsidiaryService.getByIdAsync(item.subsidiary).then((res) => {
          const sub = res as Subsidiary;
          setSubsidiary(sub);
        });

        loadDocs(driverId).catch(() => {
          enqueueSnackbar('Não foi possível carregar os documentos', { variant: 'error' });
        });

        driverService.getTerms().then((res) => {
          setTerms(res);
        });
      }).catch(() => {
        enqueueSnackbar('Motorista não encontrado', { variant: 'error' });
        onClose();
      });
    }

    return () => {
      active = false;
    };
  }, [driverId, open, onClose]);

  const changeStatus = () => {
    if (!model) return;

    driverService.changeStatus(driverId).then(() => {
      if (model.disabledAt) {
        setModel({ ...model, disabledAt: null, enabledAt: new Date() });
      } else {
        setModel({ ...model, disabledAt: new Date() });
      }
    }).catch((e) => {
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
    });
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / (k ** i)).toFixed(dm))} ${sizes[i]}`;
  };

  // Funções de análise de documentos (mantidas do código antigo)
  const handleModalJustification = (e: any) => {
    e.preventDefault();
    handleAnalyseDocuments(false);
    setOpenModalJustification(false);
  };

  const unlockDriver = (clicks: number) => {
    if (!model) return;
    const count = clicks;
    if (count > 1) {
      setCountClicks(count - 1);
    } else {
      driverService.unLockDriver(driverId).then(async () => {
        setModel({ ...model, lockedUntil: null });
        const params = {
          title: 'Aceitar novas chamadas',
          text: `Olá ${user?.displayName} tudo bem? Seguinte, a gente deseja mesmo é que você continue `
            + 'fazendo suas entregas sem problemas ou impedimentos, assim cancelei o tempo de espera '
            + 'e você já pode aceitar novas chamadas 👍🏼 👊🏼.',
          driverId,
          driverName: model.name
        };
        await driverService.sendNotification(params.title, params.text, params.driverId, params.driverName);
        setCountClicks(3);
      });
    }
  };

  const handleAnalyseDocuments = (status: boolean) => {
    if (!model) return;
    setIsSubmitting(true);
    driverService.analyseDocs(driverId, status, user?.uid!, justification).then((data: string | null) => {
      let option: any = { variant: 'success' };
      let message = `Documentos ${status ? 'aprovados' : 'recusados'} com sucesso`;
      if (data) {
        option = { variant: 'warning' };
        message = data.toString();
      }

      const result = { ...model };
      result.document.approved = status;
      setModel(result);
      enqueueSnackbar(message, option);
    }).catch((e) => {
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
    }).finally(() => {
      setIsSubmitting(false);
      setJustification('');
    });
  };

  const handleCancel = () => { setOpenConfirm(false); };
  const handleCloseTermModal = () => { setOpenModal(false); };

  const handleCopyIdToClipboard = async () => {
    if (driverId) {
      try {
        await navigator.clipboard.writeText(driverId);
        enqueueSnackbar('ID copiado para a área de transferência', { variant: 'success' });
      } catch (err) {
        enqueueSnackbar('Erro ao copiar ID', { variant: 'error' });
      }
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !model) return;

    const isJpegMime = file.type === 'image/jpeg';
    const isJpegExt = /\.(jpe?g)$/i.test(file.name);
    if (!isJpegMime && !isJpegExt) {
      enqueueSnackbar('Formato inválido. Use apenas imagens JPG/JPEG.', { variant: 'error' });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      enqueueSnackbar('A imagem deve ter no máximo 5MB.', { variant: 'error' });
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const newPhotoURL = await driverService.updatePhoto(driverId, file);
      setModel({ ...model, photoURL: newPhotoURL });
      enqueueSnackbar('Foto atualizada com sucesso!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar foto. Tente novamente.', { variant: 'error' });
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDocsChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !model || !docType) return;

    const isJpeg = file.type === 'image/jpeg' || /\.jpe?g$/i.test(file.name);
    if (!isJpeg) {
      enqueueSnackbar('Formato inválido. Use apenas imagens JPG/JPEG.', { variant: 'error' });
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      enqueueSnackbar('A imagem deve ter no máximo 10MB.', { variant: 'error' });
      return;
    }

    setIsUploadingDocs(true);
    try {
      await driverService.uploadDocument(driverId, file, docType);
      await loadDocs(driverId);
      setModel({
        ...model,
        document: {
          approved: model.document?.approved ?? null,
          analyzedBy: model.document?.analyzedBy ?? null,
          analyzedAt: model.document?.analyzedAt ?? null,
          sendedAt: new Date()
        }
      });
      enqueueSnackbar(`${documentLabel(docType)} enviado com sucesso!`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Erro ao enviar documento. Tente novamente.', { variant: 'error' });
    } finally {
      setIsUploadingDocs(false);
      if (docsInputRef.current) {
        docsInputRef.current.value = '';
      }
    }
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    await driverService.delete(driverId).then((result) => {
      handleCancel();
      if (result === 'anonymized') {
        enqueueSnackbar('Condutor anonimizado. O cadastro permanece vinculado às entregas, sem os dados pessoais.', { variant: 'success' });
      } else {
        enqueueSnackbar('Motorista removido com sucesso', { variant: 'success' });
      }
      onClose();
    }).catch((e) => {
      enqueueSnackbar(e.message, { variant: 'error' });
    }).finally(() => setIsLoading(false));
  };

  const deleteAction = () => {
    setOpenConfirm(true);
  };

  const actions = [
    { icon: <EditIcon />, name: 'Editar', action: onEdit },
    { icon: <DeleteForeverIcon />, name: 'Excluir', action: deleteAction }
  ];

  const handleOpenTermModal = (item: any) => {
    if (item) {
      setModalData(item);
      setOpenModal(true);
    }
  };

  if (!model) return null;

  const currentRating = ratingAverage?.driverId === driverId ? ratingAverage : null;
  const ratingStatus = formatDriverRatingStatus(currentRating, ratingUnavailable);
  const showStars = Boolean(currentRating && currentRating.totalRatings > 0);
  const ratingLabel = showStars && currentRating ? formatDriverRatingLabel(currentRating) : '';

  return (
    <>
      <ConfirmDialog
        open={openConfirm}
        handleClose={handleCancel}
        handleAction={handleConfirmDelete}
        isLoading={isLoading}
        message="Deseja remover este motorista?"
      />

      <TermModalComponent
        open={openModal}
        handleClose={handleCloseTermModal}
        data={modalData}
        subsidiary={subsidiary}
        driver={model}
      />

      <Dialog
        open={openModalJustification}
        onClose={() => setOpenModalJustification(false)}
        aria-labelledby="form-dialog-title"
      >
        <form autoComplete="off" onSubmit={handleModalJustification}>
          <DialogTitle id="form-dialog-title">Justificativa do cancelamento</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Informe para o condutor qual foi o motivo para não aprovar o cadastro dele na plataforma.
            </DialogContentText>
            <TextField
              autoFocus
              multiline
              fullWidth
              required
              label="Justificativa"
              variant="outlined"
              rows="4"
              value={justification}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJustification(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModalJustification(false)} color="primary">Cancelar</Button>
            <Button type="submit" color="primary">Recusar</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        fullScreen={false}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Detalhes do Motorista</span>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={7} md={6}>
              <div className="bg-midnight-bloom p-3 rounded text-white h-100">
                <div className="d-flex align-items-start justify-content-between">
                  <div className="avatar-icon-wrapper d-100">
                    <span className={`badge-circle badge badge-${model.available ? 'success' : 'warning'}`}>
                      {model.available ? 'Online' : 'Offline'}
                    </span>
                    <div className="avatar-icon d-100" style={{ position: 'relative', overflow: 'hidden' }}>
                      <img alt="" src={model.photoURL ?? avatarImg} />
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePhotoChange}
                        accept="image/jpeg,.jpg,.jpeg"
                        style={{ display: 'none' }}
                      />
                      <IconButton
                        onClick={handlePhotoClick}
                        disabled={isUploadingPhoto}
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '28px',
                          borderRadius: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)'
                          }
                        }}
                      >
                        {isUploadingPhoto ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CameraAltIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </div>
                  </div>
                  <Switch
                    checked={!model.disabledAt}
                    onChange={changeStatus}
                    color="primary"
                    title={model.disabledAt ? 'Inativo' : 'Ativo'}
                  />
                </div>
                <div
                  className="font-weight-bold font-size-lg d-flex align-items-center mt-2 mb-0"
                  role="button"
                  tabIndex={0}
                  onClick={handleCopyIdToClipboard}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCopyIdToClipboard();
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  title="Clique para copiar o ID"
                >
                  <span>{model.name}</span>
                </div>
                <p className={`font-size-md text-white-50 ${(ratingStatus || showStars) ? 'mb-1' : 'mb-4'}`}>{model.email}</p>
                {ratingStatus && (
                  <p className="mb-4 font-size-md text-white-50">{ratingStatus}</p>
                )}
                {showStars && (
                  <Box className="mb-4" title={ratingLabel}>
                    <Rating
                      readOnly
                      max={5}
                      precision={0.5}
                      value={Number(currentRating?.averageRating)}
                      aria-label={ratingLabel}
                      sx={{
                        color: '#f5c518',
                        '& .MuiRating-iconEmpty': { color: 'rgba(255,255,255,0.35)' }
                      }}
                    />
                  </Box>
                )}
                <div className="divider opacity-3 my-4" />
                <p className="font-weight-bold">{model.address.toString()}</p>
                <p>
                  Categoria:
                  {' '}
                  <span className="font-weight-bold">{CategoryDriverText[model.category]}</span>
                </p>
                <p>
                  Data de aniversário:
                  {' '}
                  <span className="font-weight-bold">{toBrazilDate(model.dtBirth)}</span>
                </p>
                <p>
                  CPF:
                  {' '}
                  <span className="font-weight-bold">{cpfOrCnpj(model.cpf)}</span>
                </p>
                <p>
                  RG:
                  {' '}
                  <span className="font-weight-bold">{model.rg}</span>
                </p>
                <p>
                  PIX:
                  {' '}
                  <span
                    className="font-weight-bold"
                  >
                    {model.pix ? `${model.pix.split('|')[0]}: ${model.pix.split('|')[1]} ` : 'Não cadastrado '}
                    <Button
                      style={{ width: '40px', height: '21px' }}
                      hidden={!model?.pix}
                      variant="contained"
                      color="primary"
                      onClick={() => navigator.clipboard.writeText(model?.pix ? model.pix.split('|')[1].toString() : '')}
                    >
                      Copiar
                    </Button>
                  </span>
                </p>

                <p>
                  Confirmação do email:
                  {' '}
                  <span className="font-weight-bold">{model.emailVerified ? 'Ok' : 'Pendente'}</span>
                </p>
                <p>
                  Status do cadastro:
                  {' '}
                  <span className="font-weight-bold">{StatusDriverText[model.status]}</span>
                </p>
                <p>
                  Criado em:
                  {' '}
                  <span className="font-weight-bold">{toBrazilDateTime(model.createdAt)}</span>
                </p>
                <p>
                  Última interacão com o sistema:
                  {' '}
                  <span className="font-weight-bold">
                    {model.updatedAt ? toBrazilDateTime(model.updatedAt) : 'Indefinido'}
                  </span>
                </p>
                <p>
                  Dispositivo:
                  {' '}
                  <span className="font-weight-bold">
                    {model.device ? `${model?.device?.manufacturer} ${model?.device?.model}
                    ${model?.device?.platform} ${model?.device?.osVersion} | versão app ${model?.device?.appVersion}` : 'Indefinido'}
                  </span>
                </p>
                <p className="warning">
                  Inibido até:
                  {' '}
                  <span className="font-weight-bold">
                    {model.lockedUntil ? `${toBrazilDateTime(model.lockedUntil)} ` : 'Sem inibição'}
                    <Button
                      style={{ height: '21px' }}
                      hidden={!model?.lockedUntil}
                      variant="contained"
                      color="primary"
                      onClick={() => unlockDriver(countClicks)}
                    >
                      {countClicks}
                      {' '}
                      cliques para liberar
                    </Button>
                  </span>
                </p>

                {/* Seção de análise de documentos (mantida do código antigo) */}
                {docs.length > 0
                  ? (
                    <Grid container spacing={3}>
                      <Grid item xs>
                        <Button
                          fullWidth
                          variant="contained"
                          color="error"
                          disabled={isSubmitting || model.document.approved === false}
                          onClick={() => setOpenModalJustification(true)}
                        >
                          Recusar documentos
                        </Button>
                        {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                      </Grid>
                      <Grid item xs>
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          disabled={isSubmitting || model?.document?.approved === true}
                          onClick={() => handleAnalyseDocuments(true)}
                        >
                          Aprovar documentos
                        </Button>
                        {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                      </Grid>
                    </Grid>
                  )
                  : (
                    <p>
                      Situação dos documentos enviados:
                      {' '}
                      <span className="font-weight-bold">{model.document.approved ? 'Aprovados' : 'Recusados'}</span>
                    </p>
                  )}
                {model.disabledAt && (
                  <p>
                    {`Desativado em: ${toBrazilDateTime(model.disabledAt)}`}
                  </p>
                )}
                {model?.enabledAt && !model.disabledAt && (
                  <p>
                    {`Reativado em: ${toBrazilDateTime(model?.enabledAt)}`}
                  </p>
                )}

                <div className="divider opacity-3 my-4" />
                <Grid container spacing={3} className="font-size-xs">
                  <Grid item xs={12} sm={12} md={12}>
                    <a target="_blank" href={`https://wa.me/${model.phoneNumber}`} rel="noreferrer">
                      <Card className="text-center my-2 p-3">
                        <div>
                          <FontAwesomeIcon icon={['fab', 'whatsapp']} className="font-size-xxl text-success" />
                        </div>
                        <div className="mt-2 line-height-sm">
                          <b className="font-size-lg text-black">{phoneNumber(model.phoneNumber!)}</b>
                          <span className="text-black-50 d-block">whatsapp</span>
                        </div>
                      </Card>
                    </a>
                  </Grid>
                  {/* <Grid item xs={12} sm={12} md={6}>
                    <Card className="text-center my-2 p-3">
                      <div>
                        <FontAwesomeIcon icon={['far', 'chart-bar']} className="font-size-xxl text-danger" />
                      </div>
                      <div className="mt-2 line-height-sm">
                        <b className="font-size-lg text-black">{model.rating?.quantity ?? 0}</b>
                        <span className="text-black-50 d-block">entregas</span>
                      </div>
                    </Card>
                  </Grid> */}
                </Grid>
              </div>
            </Grid>
            <Grid item xs={12} sm={5} md={6}>
              <h5 className="font-size-sm text-uppercase text-black-50 font-weight-bold my-4">
                {`Documentos enviados em ${toBrazilDateTime(model.document?.sendedAt)}`}
              </h5>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <FormControl size="small" sx={{ minWidth: 240 }} disabled={isUploadingDocs}>
                  <InputLabel id="driver-doc-type-label">Documento</InputLabel>
                  <Select
                    labelId="driver-doc-type-label"
                    label="Documento"
                    value={docType}
                    onChange={(event) => setDocType(event.target.value)}
                  >
                    {DRIVER_DOCUMENTS.map((item) => (
                      <MenuItem key={item.fileName} value={item.fileName}>{item.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <input
                  type="file"
                  ref={docsInputRef}
                  onChange={handleDocsChange}
                  accept="image/jpeg,.jpg,.jpeg"
                  style={{ display: 'none' }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isUploadingDocs || !docType}
                  onClick={() => docsInputRef.current?.click()}
                  startIcon={isUploadingDocs ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  Enviar
                </Button>
              </Box>
              <Grid container spacing={3}>
                {docs.length
                  ? (
                    <>
                      {docs.map((x) => (
                        <Grid key={x.id} item xs={12}>
                          <a
                            href={x.url}
                            rel="noreferrer"
                            target="_blank"
                            className="card card-box btn rounded text-left mb-4 p-3"
                          >
                            <div className="d-flex align-items-center flex-column flex-sm-row">
                              <div>
                                <div className="text-center text-warning display-2 d-50 rounded-circle mb-3 mb-sm-2">
                                  <FontAwesomeIcon icon={x.icon} />
                                </div>
                              </div>
                              <div className="pl-0 pl-sm-3">
                                <div className="d-block text-center d-sm-flex align-items-center">
                                  <span className="font-size-md">{documentLabel(x.name)}</span>
                                </div>
                                <p className="text-black-50 opacity-8 mb-0" title="Data de modificação">
                                  {`${toBrazilDateTime(x.updated)} | ${formatBytes(x.size)}`}
                                </p>
                              </div>
                            </div>
                          </a>
                        </Grid>
                      ))}
                    </>
                  )
                  : <NoData message="Nenhum documento encontrado." />}
              </Grid>
              <h5 className="font-size-sm text-uppercase text-black-50 font-weight-bold my-4">
                Termos e Contratos
              </h5>
              <Grid container spacing={3}>
                {terms.length
                  ? (
                    <>
                      {terms.map((x) => (
                        <Grid key={x.id} item xs={12}>
                          <button
                            type="button"
                            className="card card-box btn rounded text-left mb-4 p-3 w-100 border-0 bg-transparent"
                            onClick={() => handleOpenTermModal(x)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center flex-column flex-sm-row">
                              <div>
                                <div className="text-center text-warning display-2 d-50 rounded-circle mb-3 mb-sm-2">
                                  <FontAwesomeIcon icon={['fas', 'file-signature']} />
                                </div>
                              </div>
                              <div className="pl-0 pl-sm-3">
                                <div className="d-block text-left d-sm-flex align-items-left">
                                  <span className="font-size-md">{x.description}</span>
                                </div>
                                <p className="text-black-50 opacity-8 mb-0" title="Data da aceitação">
                                  {`Data e hora do aceite ${toBrazilDateTime(model.createdAt)}`}
                                </p>
                              </div>
                            </div>
                          </button>
                        </Grid>
                      ))}
                    </>
                  )
                  : <NoData message="Nenhum termo de uso encontrado." />}
              </Grid>
              <SpeedDial
                ariaLabel="Opções"
                sx={{ position: 'absolute', bottom: 16, right: 16 }}
                direction="left"
                icon={<SettingsIcon />}
              >
                {actions.map((action) => (
                  <SpeedDialAction
                    key={action.name}
                    icon={action.icon}
                    tooltipTitle={action.name}
                    onClick={action.action}
                  />
                ))}
              </SpeedDial>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DriverDetailsModal;
